import type { Block, BlockStyle } from "@alchimie/shared";
import type { CSSProperties } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, type Asset } from "../api.js";

/**
 * Rendu display d'un bloc Alchimie — utilisé dans le Canvas du Builder
 * et (en miroir, SSR) sur les pages de vente publiques.
 *
 * Pas d'interaction d'édition ici — le Builder enveloppe et gère sélection/hover.
 */
export function BlockRenderer({ block, inColumn }: { block: Block; inColumn?: boolean }) {
  const style = styleToCss(block.style);
  const animClass = block.style?.enterAnimation && block.style.enterAnimation !== "none"
    ? `alchimie-anim-${block.style.enterAnimation}`
    : undefined;
  return (
    <>
      {animClass && <AnimationStyles />}
      <div
        className={animClass}
        style={{
          ...style,
          animationDelay: block.style?.enterDelay ? `${block.style.enterDelay}ms` : undefined,
          animationFillMode: animClass ? "both" : undefined,
        }}
      >
        {renderInner(block, inColumn)}
      </div>
    </>
  );
}

// Quand un bloc est rendu dans une colonne, on lui retire son maxWidth + padding
// horizontal pleine-page (sinon il déborde / ne tient pas dans la colonne).
function container(inColumn: boolean | undefined, max: number): CSSProperties {
  if (inColumn) return { maxWidth: "100%", margin: 0, padding: 0 };
  return { maxWidth: max, margin: "0 auto", padding: "0 24px" };
}

/** Injecté une seule fois grâce au :where(:not(:has(~))) côté <style>. */
function AnimationStyles() {
  return (
    <style>{`
      @keyframes alchimie-fade-in { from { opacity: 0 } to { opacity: 1 } }
      @keyframes alchimie-slide-up { from { opacity: 0; transform: translateY(24px) } to { opacity: 1; transform: translateY(0) } }
      @keyframes alchimie-slide-left { from { opacity: 0; transform: translateX(-24px) } to { opacity: 1; transform: translateX(0) } }
      @keyframes alchimie-scale-in { from { opacity: 0; transform: scale(0.94) } to { opacity: 1; transform: scale(1) } }
      .alchimie-anim-fade { animation: alchimie-fade-in 0.7s ease-out }
      .alchimie-anim-slide-up { animation: alchimie-slide-up 0.7s cubic-bezier(0.16,1,0.3,1) }
      .alchimie-anim-slide-left { animation: alchimie-slide-left 0.7s cubic-bezier(0.16,1,0.3,1) }
      .alchimie-anim-scale { animation: alchimie-scale-in 0.6s cubic-bezier(0.16,1,0.3,1) }
    `}</style>
  );
}

function renderInner(b: Block, inColumn?: boolean): React.ReactNode {
  switch (b.type) {
    case "HERO":
      return (
        <div style={container(inColumn, 760)}>
          <h1 style={{ fontSize: 42, lineHeight: 1.15, margin: "0 0 16px", fontWeight: 700, color: "inherit" }}>
            {b.props.title}
          </h1>
          {b.props.subtitle && (
            <p style={{ fontSize: 20, ...subtitleColor(b.style), margin: 0 }}>{b.props.subtitle}</p>
          )}
          {b.props.ctaLabel && (
            <div style={{ marginTop: 32 }}>
              <button style={primaryBtn}>{b.props.ctaLabel}</button>
            </div>
          )}
        </div>
      );

    case "RICH_TEXT":
      return (
        <div style={{ ...container(inColumn, 720), lineHeight: 1.7, fontSize: 17 }}>
          {renderTiptapDoc(b.props.doc)}
        </div>
      );

    case "VIDEO":
      return (
        <div style={container(inColumn, 920)}>
          <div
            style={{
              position: "relative",
              paddingBottom: "56.25%",
              background: "#1a1a1a",
              borderRadius: 12,
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff8",
              fontStyle: "italic",
            }}
          >
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {b.props.assetId ? "▶ Vidéo prête à lire" : "— Sélectionnez une vidéo —"}
            </div>
          </div>
          {b.props.caption && (
            <div style={{ textAlign: "center", ...subtitleColor(b.style), fontSize: 14, marginTop: 12 }}>{b.props.caption}</div>
          )}
        </div>
      );

    case "AUDIO":
      return (
        <div style={container(inColumn, 720)}>
          <div style={{ background: "#1a1a1a", color: "#fff", padding: 20, borderRadius: 12, display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ fontSize: 24 }}>♫</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>{b.props.title ?? "Piste audio"}</div>
              <div style={{ fontSize: 13, opacity: 0.7 }}>{b.props.assetId ? "Audio prêt" : "Sélectionnez un fichier"}</div>
            </div>
            <button style={{ ...primaryBtn, padding: "8px 16px" }}>▶</button>
          </div>
        </div>
      );

    case "PDF":
      return (
        <div style={container(inColumn, 720)}>
          <div style={{ border: "1px solid #ddd", borderRadius: 12, padding: 20, display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ fontSize: 32 }}>📄</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>{b.props.title ?? "Document PDF"}</div>
              <div style={{ fontSize: 13, ...subtitleColor(b.style) }}>{b.props.assetId ? "PDF prêt" : "Sélectionnez un fichier"}</div>
            </div>
            {b.props.downloadable && <button style={ghostBtn}>Télécharger</button>}
          </div>
        </div>
      );

    case "IMAGE":
      return <ImageBlockRenderer assetId={b.props.assetId} alt={b.props.alt} caption={b.props.caption} inColumn={inColumn} />;

    case "BENEFITS":
      return (
        <div style={container(inColumn, 920)}>
          {b.props.heading && (
            <h2 style={{ textAlign: "center", fontSize: 28, fontWeight: 700, margin: "0 0 32px" }}>{b.props.heading}</h2>
          )}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${b.props.columns}, 1fr)`,
              gap: 24,
            }}
          >
            {b.props.items.map((it, i) => (
              <div key={i} style={{ background: "#fff", border: "1px solid #ece8e0", borderRadius: 10, padding: 24 }}>
                {it.icon && <div style={{ fontSize: 28, marginBottom: 12 }}>{it.icon}</div>}
                <div style={{ fontWeight: 600, fontSize: 17, marginBottom: 8 }}>{it.title}</div>
                {it.body && <div style={{ ...subtitleColor(b.style), fontSize: 15 }}>{it.body}</div>}
              </div>
            ))}
          </div>
        </div>
      );

    case "TESTIMONIALS":
      return (
        <div style={container(inColumn, 920)}>
          {b.props.heading && (
            <h2 style={{ textAlign: "center", fontSize: 28, fontWeight: 700, margin: "0 0 32px" }}>{b.props.heading}</h2>
          )}
          <div style={{ display: "grid", gap: 20 }}>
            {b.props.items.map((it, i) => (
              <figure key={i} style={{ background: "#fff", border: "1px solid #ece8e0", borderRadius: 10, padding: 24, margin: 0 }}>
                <blockquote style={{ margin: "0 0 12px", fontStyle: "italic", fontSize: 17 }}>« {it.quote} »</blockquote>
                <cite style={{ ...subtitleColor(b.style), fontSize: 14, fontStyle: "normal" }}>
                  — {it.author}
                  {it.role && <span style={{ opacity: 0.7 }}> · {it.role}</span>}
                </cite>
              </figure>
            ))}
          </div>
        </div>
      );

    case "FAQ":
      return (
        <div style={container(inColumn, 720)}>
          {b.props.heading && (
            <h2 style={{ textAlign: "center", fontSize: 28, fontWeight: 700, margin: "0 0 32px" }}>{b.props.heading}</h2>
          )}
          {b.props.items.map((it, i) => (
            <div key={i} style={{ borderBottom: "1px solid #ece8e0", padding: "18px 0" }}>
              <h4 style={{ margin: "0 0 8px", fontSize: 17, fontWeight: 600 }}>{it.q}</h4>
              <p style={{ margin: 0, ...subtitleColor(b.style) }}>{it.a}</p>
            </div>
          ))}
        </div>
      );

    case "PRICING":
      return (
        <div style={container(inColumn, 600)}>
          <div style={{ background: "#1a1a1a", color: "#fff", padding: 48, borderRadius: 16, textAlign: "center" }}>
            {b.props.heading && <div style={{ fontSize: 20, opacity: 0.85 }}>{b.props.heading}</div>}
            <div style={{ fontSize: 48, fontWeight: 700, margin: "16px 0" }}>— €</div>
            <div style={{ fontSize: 13, opacity: 0.6, marginBottom: 24 }}>(prix de l'Offre liée)</div>
            <button style={{ ...primaryBtn, background: "#fff", color: "#1a1a1a" }}>{b.props.ctaLabel}</button>
          </div>
        </div>
      );

    case "CTA":
      return (
        <div style={{ ...container(inColumn, 720), textAlign: "center" }}>
          {b.props.subtitle && <p style={{ ...subtitleColor(b.style), fontSize: 17, marginTop: 0, marginBottom: 32 }}>{b.props.subtitle}</p>}
          <button style={primaryBtn}>{b.props.label}</button>
        </div>
      );

    case "DIVIDER": {
      const color = b.props.color ?? "#e0dcd0";
      const thickness = b.props.thickness ?? "1px";
      const lineStyle = b.props.style ?? "solid";
      return (
        <div style={container(inColumn, 720)}>
          <hr style={{ border: 0, borderTop: `${thickness} ${lineStyle} ${color}` }} />
        </div>
      );
    }

    case "SPACER":
      return <div style={{ height: b.props.height }} />;

    case "COLUMNS": {
      const n = b.props.columns.length;
      const widths = b.props.widths && b.props.widths.length === n ? b.props.widths : Array(n).fill(1);
      const gap = b.props.gap ?? "24px";
      return (
        <div style={container(inColumn, 1080)}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: widths.map((w) => `minmax(0, ${w}fr)`).join(" "),
              gap,
              alignItems: "start",
            }}
          >
            {b.props.columns.map((col, i) => (
              <div key={i} style={{ minHeight: 80, minWidth: 0 }}>
                {col.length === 0 ? (
                  <div
                    style={{
                      border: "1px dashed #d6d2c8",
                      padding: 24,
                      borderRadius: 8,
                      textAlign: "center",
                      color: "#aaa",
                      fontStyle: "italic",
                      fontSize: 13,
                    }}
                  >
                    Colonne vide
                  </div>
                ) : (
                  col.map((nested) => <BlockRenderer key={nested.id} block={nested} inColumn />)
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }

    case "CUSTOM_HTML":
      return <div dangerouslySetInnerHTML={{ __html: b.props.html }} />;
  }
}

// ─────────────────────────────────────────────────────────────
// Renderer IMAGE — fetch l'asset et l'affiche réellement
// ─────────────────────────────────────────────────────────────
function ImageBlockRenderer({
  assetId,
  alt,
  caption,
  inColumn,
}: {
  assetId: string;
  alt?: string;
  caption?: string;
  inColumn?: boolean;
}) {
  const asset = useQuery({
    queryKey: ["asset", assetId],
    queryFn: () => api.get<Asset>(`/assets/${assetId}`),
    enabled: !!assetId,
  });

  return (
    <div style={{ ...container(inColumn, 920), textAlign: "center" }}>
      {asset.data?.cdnUrl ? (
        <img
          src={asset.data.cdnUrl}
          alt={alt ?? asset.data.name}
          style={{ maxWidth: "100%", height: "auto", borderRadius: 12, display: "block", margin: "0 auto" }}
        />
      ) : (
        <div
          style={{
            background: "#f0f0ec",
            minHeight: 240,
            borderRadius: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#888",
            fontStyle: "italic",
          }}
        >
          {assetId ? "Chargement…" : "— Sélectionnez une image —"}
        </div>
      )}
      {caption && <div style={{ marginTop: 12, fontSize: 14, opacity: 0.65, color: "inherit" }}>{caption}</div>}
    </div>
  );
}

// Couleur des sous-titres / textes secondaires : si l'utilisateur a choisi une
// `subtitleColor`, on l'utilise telle quelle. Sinon on hérite de la couleur de
// texte du bloc (textColor) avec une légère opacité pour la hiérarchie visuelle.
function subtitleColor(s?: BlockStyle): CSSProperties {
  if (s?.subtitleColor) return { color: s.subtitleColor };
  return { color: "inherit", opacity: 0.72 };
}

// ─────────────────────────────────────────────────────────────
// Style block → CSS properties
// ─────────────────────────────────────────────────────────────
export function styleToCss(s?: BlockStyle): CSSProperties {
  if (!s) return {};
  const css: CSSProperties = {
    paddingTop: s.paddingTop,
    paddingBottom: s.paddingBottom,
    paddingLeft: s.paddingLeft,
    paddingRight: s.paddingRight,
    marginTop: s.marginTop,
    marginBottom: s.marginBottom,
    backgroundColor: s.backgroundColor,
    color: s.textColor,
    textAlign: s.textAlign,
    fontFamily: s.fontFamily,
    borderRadius: s.borderRadius,
    borderColor: s.borderColor,
    borderWidth: s.borderWidth,
    borderStyle: s.borderWidth ? "solid" : undefined,
  };
  if (s.backgroundImage) {
    css.backgroundImage = s.backgroundOverlay
      ? `linear-gradient(${s.backgroundOverlay}, ${s.backgroundOverlay}), url(${s.backgroundImage})`
      : `url(${s.backgroundImage})`;
    css.backgroundSize = s.backgroundSize ?? "cover";
    css.backgroundPosition = s.backgroundPosition ?? "center";
  }
  if (s.maxWidth) {
    css.maxWidth = s.maxWidth;
    css.marginLeft = "auto";
    css.marginRight = "auto";
  }
  // Nettoie les undefined
  for (const k of Object.keys(css) as (keyof CSSProperties)[]) {
    if (css[k] === undefined) delete css[k];
  }
  return css;
}

// ─────────────────────────────────────────────────────────────
// Tiptap doc renderer — couvre les nodes du StarterKit utilisés par la
// toolbar du TiptapEditor : paragraph, heading (1-3), bulletList,
// orderedList, listItem, blockquote, hardBreak, text + marks (bold,
// italic, strike, code, link).
// ─────────────────────────────────────────────────────────────
type TiptapNode = {
  type?: string;
  text?: string;
  attrs?: Record<string, unknown>;
  marks?: { type?: string; attrs?: Record<string, unknown> }[];
  content?: TiptapNode[];
};

function renderTiptapDoc(doc: unknown): React.ReactNode {
  if (!doc || typeof doc !== "object") return null;
  const d = doc as TiptapNode;
  if (d.type !== "doc" || !Array.isArray(d.content)) return null;
  return d.content.map((node, i) => renderTiptapNode(node, `n-${i}`));
}

function renderTiptapNode(node: TiptapNode, key: string): React.ReactNode {
  if (!node) return null;
  switch (node.type) {
    case "paragraph":
      return <p key={key} style={{ margin: "0 0 16px" }}>{renderInline(node.content)}</p>;
    case "heading": {
      const level = Number(node.attrs?.level ?? 2);
      const Tag = (`h${Math.min(Math.max(level, 1), 6)}`) as "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
      const sizes: Record<string, number> = { h1: 32, h2: 26, h3: 21, h4: 18, h5: 16, h6: 15 };
      return (
        <Tag
          key={key}
          style={{ fontSize: sizes[Tag], fontWeight: 700, lineHeight: 1.25, margin: "20px 0 12px" }}
        >
          {renderInline(node.content)}
        </Tag>
      );
    }
    case "bulletList":
      return (
        <ul key={key} style={{ margin: "0 0 16px", paddingLeft: 24 }}>
          {(node.content ?? []).map((c, i) => renderTiptapNode(c, `${key}-${i}`))}
        </ul>
      );
    case "orderedList":
      return (
        <ol
          key={key}
          start={typeof node.attrs?.start === "number" ? (node.attrs.start as number) : 1}
          style={{ margin: "0 0 16px", paddingLeft: 24 }}
        >
          {(node.content ?? []).map((c, i) => renderTiptapNode(c, `${key}-${i}`))}
        </ol>
      );
    case "listItem":
      return (
        <li key={key} style={{ margin: "0 0 4px" }}>
          {(node.content ?? []).map((c, i) => renderTiptapNode(c, `${key}-${i}`))}
        </li>
      );
    case "blockquote":
      return (
        <blockquote
          key={key}
          style={{ borderLeft: "3px solid #d6d2c8", paddingLeft: 12, margin: "0 0 16px", color: "inherit", opacity: 0.85, fontStyle: "italic" }}
        >
          {(node.content ?? []).map((c, i) => renderTiptapNode(c, `${key}-${i}`))}
        </blockquote>
      );
    case "horizontalRule":
      return <hr key={key} style={{ border: 0, borderTop: "1px solid #e0dcd0", margin: "16px 0" }} />;
    case "codeBlock":
      return (
        <pre key={key} style={{ background: "#f4f1ea", padding: 12, borderRadius: 6, margin: "0 0 16px", overflowX: "auto" }}>
          <code>{renderInline(node.content)}</code>
        </pre>
      );
    default:
      // Fallback: rends quand même les enfants pour ne rien faire disparaître.
      if (Array.isArray(node.content)) {
        return <span key={key}>{node.content.map((c, i) => renderTiptapNode(c, `${key}-${i}`))}</span>;
      }
      return null;
  }
}

function renderInline(content?: TiptapNode[]): React.ReactNode {
  if (!Array.isArray(content)) return null;
  return content.map((c, j) => {
    if (c.type === "hardBreak") return <br key={`br-${j}`} />;
    if (c.type !== "text" && c.type !== undefined) {
      // Node inline inconnu : rend récursif au cas où.
      return <span key={`x-${j}`}>{renderTiptapNode(c, `x-${j}`)}</span>;
    }
    const marks = (c.marks ?? []);
    let el: React.ReactNode = c.text ?? "";
    for (const m of marks) {
      if (m.type === "bold") el = <strong>{el}</strong>;
      else if (m.type === "italic") el = <em>{el}</em>;
      else if (m.type === "strike") el = <s>{el}</s>;
      else if (m.type === "code") el = <code style={{ background: "#f4f1ea", padding: "1px 4px", borderRadius: 3, fontSize: "0.92em" }}>{el}</code>;
      else if (m.type === "link") {
        const href = typeof m.attrs?.href === "string" ? m.attrs.href : "#";
        const target = typeof m.attrs?.target === "string" ? m.attrs.target : undefined;
        el = <a href={href} target={target} rel={target === "_blank" ? "noopener noreferrer" : undefined} style={{ color: "inherit", textDecoration: "underline" }}>{el}</a>;
      }
    }
    return <span key={`t-${j}`}>{el}</span>;
  });
}

// ─────────────────────────────────────────────────────────────
// Boutons partagés
// ─────────────────────────────────────────────────────────────
const primaryBtn: CSSProperties = {
  padding: "16px 36px",
  background: "#1a1a1a",
  color: "#fff",
  border: "none",
  borderRadius: 8,
  fontSize: 17,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
};

const ghostBtn: CSSProperties = {
  padding: "10px 18px",
  background: "transparent",
  color: "#1a1a1a",
  border: "1px solid #ddd",
  borderRadius: 6,
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
};
